import React from 'react';
import {
  Alert, Badge, Button, Checkbox, EmptyState, ErrorState, PageHeader, SkeletonRow, Table, TextInput,
  useToast,
} from '@parsina/framework';
import { ArrowLeftRight, Search, SearchX, Unlink, UserSearch } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useShortcut } from '../../lib/shortcuts';
import { EMPTY } from '../../lib/format';
import {
  CONSOLIDATE_PATIENT_ACCOUNTS, CONSOLIDATION_PREVIEW_QUERY, PATIENT_ACCOUNTS_QUERY, RELEASE_PATIENT_ACCOUNT,
} from './queries';
import {
  AttributeBadge, EnabledBadge, Ident, accountName, dropBlockers, initialOrientation, recordsOf,
  searchVariables,
} from './shared';
import ConsolidationPreview, { RoleList } from './ConsolidationPreview';
import ConsolidationResult, { AuditLine } from './ConsolidationResult';
import ConsolidateDialog from './ConsolidateDialog';
import ReleaseDialog from './ReleaseDialog';

/**
 * «یکی کردن حساب‌ها» — one person, two sign-in accounts (PM-0536 P5b), and
 * releasing a record from the account that holds it (PM-0546). Contract:
 * `parsina.bff.admin/src/graphql/typeDefs/accounts.js`; platform_admin only, the
 * BFF and the service check it again on every write.
 *
 *   ① PageHeader · ② the last result + its audit line · ④ search (number / کد ملی)
 *   ⑤ the accounts found (pick two) and the records here · the side-by-side
 *   preview → «یکی کردن…» (reason + the dropped account's text typed back)
 *
 * NOTHING HERE GOES INTO THE URL. A phone number or a کد ملی in the address bar
 * lands in browser history, proxy logs and every ticket the link is pasted into;
 * the search is the operator's to type again.
 *
 * Every region has its four states (DESIGN_CONCEPT §2.4): the lookup, and the
 * preview on its own — one failing never blanks the other.
 */
export default function PatientAccounts({ me }) {
  const { t } = useTranslation();
  const toast = useToast();
  useDocumentTitle('nav.title.patientAccounts');

  // ── the lookup ────────────────────────────────────────────────────────────
  const [form, setForm] = React.useState({ phone: '', nationalId: '' });
  const [formError, setFormError] = React.useState(null);
  const [searched, setSearched] = React.useState(null);   // the variables last sent
  const [lookup, setLookup] = React.useState({ status: 'idle', data: null, error: null });
  const [selected, setSelected] = React.useState([]);     // up to two account ids

  const runLookup = React.useCallback((variables, { quiet = false } = {}) => {
    setLookup((prev) => ({ status: quiet && prev.data ? 'refreshing' : 'loading', data: quiet ? prev.data : null, error: null }));
    return gql(PATIENT_ACCOUNTS_QUERY, variables)
      .then((d) => setLookup({ status: 'content', data: d?.patientAccounts || { configured: true, accounts: [], records: [] }, error: null }))
      .catch((e) => setLookup({ status: 'error', data: null, error: errorMessage(e, t) }));
  }, [t]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const variables = searchVariables(form);
    if (!variables.phone && !variables.nationalId) {
      setFormError(t('accounts.search.needOne'));
      return;
    }
    setFormError(null);
    setSearched(variables);
    setSelected([]);
    setPreview(null);
    runLookup(variables);
  };

  const phoneRef = React.useRef(null);
  const focusSearch = () => { phoneRef.current?.focus(); phoneRef.current?.select?.(); };
  useShortcut('f3', focusSearch, { label: t('shortcuts.action.search'), group: t('shortcuts.groups.patientAccounts') });

  // ── the preview ───────────────────────────────────────────────────────────
  // { keepId, dropId, status, data, error } | null
  const [preview, setPreview] = React.useState(null);

  const loadPreview = React.useCallback((keepId, dropId) => {
    setPreview({ keepId, dropId, status: 'loading', data: null, error: null });
    return gql(CONSOLIDATION_PREVIEW_QUERY, { keepUserId: keepId, dropUserId: dropId })
      .then((d) => setPreview((p) => (p && p.keepId === keepId && p.dropId === dropId
        ? { ...p, status: 'content', data: d?.accountConsolidationPreview || null } : p)))
      .catch((e) => setPreview((p) => (p && p.keepId === keepId && p.dropId === dropId
        ? { ...p, status: 'error', error: errorMessage(e, t) } : p)));
  }, [t]);

  const accounts = lookup.data?.accounts || [];
  const byId = new Map(accounts.map((a) => [a.id, a]));

  const openPreview = () => {
    const [a, b] = selected.map((id) => byId.get(id));
    if (!a || !b) return;
    const { keepId, dropId } = initialOrientation(a, b);
    loadPreview(keepId, dropId);
  };
  /** «این حساب بماند» on the other card: keep `id`, drop the one kept so far. */
  const keepAccount = (id) => {
    if (!preview || preview.keepId === id) return;
    loadPreview(id, preview.keepId);
  };

  // ── the writes ────────────────────────────────────────────────────────────
  const [consolidating, setConsolidating] = React.useState(null);   // the preview data
  const [releasing, setReleasing] = React.useState(null);           // { record, holderLabel }
  const [pending, setPending] = React.useState(false);
  const [dialogError, setDialogError] = React.useState(null);
  const [lastEntry, setLastEntry] = React.useState(null);           // what the last write did

  const actor = me?.name || me?.username || '';

  const consolidate = async ({ reason, confirmation }) => {
    const target = consolidating;
    setPending(true);
    setDialogError(null);
    try {
      const d = await gql(CONSOLIDATE_PATIENT_ACCOUNTS, {
        keepUserId: target.keep.id, dropUserId: target.drop.id, reason, confirmation,
      });
      const report = d?.consolidatePatientAccounts;
      setConsolidating(null);
      setPreview(null);
      setSelected([]);
      setLastEntry({
        kind: 'consolidate', report, keep: target.keep, drop: target.drop, reason, actor, at: new Date().toISOString(),
      });
      if (report?.failedStep) toast.error(t('accounts.result.partialTitle'));
      else toast.success(t('accounts.result.title'));
      if (searched) runLookup(searched, { quiet: true });
    } catch (e) {
      setDialogError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const release = async ({ reason }) => {
    const target = releasing;
    setPending(true);
    setDialogError(null);
    try {
      const d = await gql(RELEASE_PATIENT_ACCOUNT, {
        patientId: target.record.id, keycloakUserId: target.record.keycloakUserId, reason,
      });
      setReleasing(null);
      setLastEntry({
        kind: 'release', report: d?.releasePatientAccount, record: target.record, holderLabel: target.holderLabel,
        reason, actor, at: new Date().toISOString(),
      });
      toast.success(t('accounts.release.success'));
      if (searched) runLookup(searched, { quiet: true });
    } catch (e) {
      setDialogError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const holderLabel = (userId) => {
    const holder = byId.get(userId);
    return holder ? accountName(holder) : userId;
  };

  return (
    <div className="flex h-full flex-col bg-background" data-testid="patient-accounts">
      <PageHeader title={t('accounts.title')} sub={t('accounts.sub')} />

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        {lastEntry?.kind === 'consolidate' ? (
          <ConsolidationResult
            entry={lastEntry}
            onRerun={() => loadPreview(lastEntry.keep.id, lastEntry.drop.id)}
            onDismiss={() => setLastEntry(null)}
          />
        ) : null}
        {lastEntry?.kind === 'release' ? (
          <Alert
            variant="success"
            title={t('accounts.release.success')}
            className="mb-4"
            data-testid="accounts-release-result"
            action={(
              <Button size="sm" variant="ghost" onClick={() => setLastEntry(null)}>
                {t('accounts.result.dismiss')}
              </Button>
            )}
          >
            <AuditLine entry={lastEntry} />
          </Alert>
        ) : null}

        <form
          onSubmit={submitSearch}
          className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3"
          role="search"
          aria-label={t('accounts.search.label')}
          data-testid="accounts-search"
          noValidate
        >
          <div className="min-w-[14rem] flex-1">
            <TextInput
              ref={phoneRef}
              label={t('accounts.search.phone')}
              dir="ltr"
              inputMode="tel"
              autoComplete="off"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target?.value ?? e }))}
              placeholder={t('accounts.search.phonePlaceholder')}
              data-testid="accounts-search-phone"
            />
          </div>
          <div className="min-w-[14rem] flex-1">
            <TextInput
              label={t('accounts.search.nationalId')}
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              value={form.nationalId}
              onChange={(e) => setForm((f) => ({ ...f, nationalId: e.target?.value ?? e }))}
              data-testid="accounts-search-national-id"
            />
          </div>
          <Button type="submit" disabled={lookup.status === 'loading'} data-testid="accounts-search-submit">
            <Search size={16} aria-hidden />
            {t('accounts.search.submit')}
          </Button>
          {formError ? (
            <p className="basis-full text-sm text-[var(--chip-danger-ink)]" role="alert" data-testid="accounts-search-error">
              {formError}
            </p>
          ) : null}
        </form>

        <Results
          lookup={lookup}
          selected={selected}
          onToggle={(id, on) => setSelected((s) => (on ? [...s, id].slice(-2) : s.filter((x) => x !== id)))}
          onRetry={() => searched && runLookup(searched)}
          onCompare={openPreview}
          onRelease={(record) => { setDialogError(null); setReleasing({ record, holderLabel: holderLabel(record.keycloakUserId) }); }}
          holderLabel={holderLabel}
        />

        {preview ? (
          <ConsolidationPreview
            status={preview.status}
            preview={preview.data}
            error={preview.error}
            onRetry={() => loadPreview(preview.keepId, preview.dropId)}
            onKeep={keepAccount}
            onConsolidate={() => { setDialogError(null); setConsolidating(preview.data); }}
            onClose={() => setPreview(null)}
          />
        ) : null}
      </div>

      <ConsolidateDialog
        preview={consolidating}
        pending={pending}
        error={dialogError}
        onOpenChange={(open) => { if (!open && !pending) setConsolidating(null); }}
        onSubmit={consolidate}
      />
      <ReleaseDialog
        target={releasing}
        pending={pending}
        error={dialogError}
        onOpenChange={(open) => { if (!open && !pending) setReleasing(null); }}
        onSubmit={release}
      />
    </div>
  );
}

/** The lookup region: idle, loading, error, not configured, empty, content. */
function Results({
  lookup, selected, onToggle, onRetry, onCompare, onRelease, holderLabel,
}) {
  const { t } = useTranslation();

  if (lookup.status === 'idle') {
    return (
      <div className="rounded-lg border border-border bg-card" data-state="idle">
        <EmptyState
          icon={<UserSearch size={44} strokeWidth={1.5} aria-hidden />}
          title={t('accounts.idle.title')}
          description={t('accounts.idle.desc')}
          data-testid="accounts-idle"
        />
      </div>
    );
  }
  if (lookup.status === 'loading') {
    return (
      <div
        className="rounded-lg border border-border bg-card"
        data-testid="accounts-loading"
        data-state="loading"
        role="status"
        aria-busy="true"
        aria-label={t('accounts.loading')}
      >
        <table className="w-full">
          <tbody>
            {/* eslint-disable-next-line react/no-array-index-key */}
            {Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} cols={7} />)}
          </tbody>
        </table>
      </div>
    );
  }
  if (lookup.status === 'error') {
    return (
      <div className="rounded-lg border border-border bg-card">
        <ErrorState
          title={t('accounts.error')}
          description={lookup.error}
          onRetry={onRetry}
          retryLabel={t('common.actions.retry')}
          data-testid="accounts-error"
        />
      </div>
    );
  }

  const data = lookup.data || {};
  if (data.configured === false) {
    return (
      <Alert variant="warning" title={t('accounts.notConfigured.title')} data-testid="accounts-not-configured">
        {t('accounts.notConfigured.desc')}
      </Alert>
    );
  }

  const accounts = data.accounts || [];
  const records = recordsOf(data);
  if (!accounts.length && !records.length) {
    return (
      <div className="rounded-lg border border-border bg-card" data-state="empty">
        <EmptyState
          icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
          title={t('accounts.empty.title')}
          description={t('accounts.empty.desc')}
          data-testid="accounts-empty"
        />
      </div>
    );
  }

  return (
    <div
      className="space-y-4 transition-opacity"
      style={{ opacity: lookup.status === 'refreshing' ? 0.45 : 1 }}
      aria-busy={lookup.status === 'refreshing' || undefined}
      data-state="content"
    >
      <AccountsTable
        accounts={accounts}
        selected={selected}
        onToggle={onToggle}
        onCompare={onCompare}
      />
      <RecordsTable records={records} onRelease={onRelease} holderLabel={holderLabel} />
    </div>
  );
}

function AccountsTable({
  accounts, selected, onToggle, onCompare,
}) {
  const { t, formatDate } = useTranslation();
  const full = selected.length >= 2;

  const columns = [
    {
      key: 'select',
      header: t('accounts.columns.select'),
      width: '4.5rem',
      render: (a) => {
        const on = selected.includes(a.id);
        return (
          <Checkbox
            checked={on}
            disabled={!on && full}
            onChange={(v) => onToggle(a.id, !!v)}
            // Inside the <label>, so it names the checkbox; the row shows the name already.
            label={<span className="sr-only">{t('accounts.columns.selectAria', { name: accountName(a) })}</span>}
            data-testid={`accounts-select-${a.id}`}
          />
        );
      },
    },
    {
      key: 'account',
      header: t('accounts.columns.account'),
      render: (a) => (
        <div className="min-w-0">
          <div dir="auto" className="truncate font-semibold text-foreground">{accountName(a)}</div>
          <Ident value={a.username} className="text-muted-foreground" />
        </div>
      ),
    },
    {
      key: 'phone',
      header: t('accounts.columns.phone'),
      width: '10rem',
      render: (a) => (
        <div className="min-w-0">
          <Ident value={a.phoneNumber} />
          {a.formerPhoneNumber ? (
            <div className="text-xs text-muted-foreground">
              {t('accounts.account.former')}
              {' '}
              <Ident value={a.formerPhoneNumber} />
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'nationalId',
      header: t('accounts.columns.nationalId'),
      width: '8rem',
      render: (a) => <Ident value={a.nationalId} />,
    },
    {
      key: 'status',
      header: t('accounts.columns.status'),
      width: '6rem',
      render: (a) => <EnabledBadge enabled={a.enabled} data-testid={`accounts-enabled-${a.id}`} />,
    },
    {
      key: 'roles',
      header: t('accounts.columns.roles'),
      render: (a) => (
        <div className="min-w-0" data-testid={`accounts-roles-${a.id}`}>
          <RoleList account={a} />
          {dropBlockers(a).length ? (
            <Badge variant="destructive" className="mt-1" data-testid={`accounts-staff-${a.id}`}>
              {t('accounts.account.neverDropped')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'records',
      header: t('accounts.columns.records'),
      width: '12rem',
      render: (a) => (
        <div className="min-w-0 text-sm">
          {a.records?.length
            ? a.records.map((r) => <div key={r.id} dir="auto" className="truncate">{r.fullName || t('accounts.records.unnamed')}</div>)
            : <span className="text-muted-foreground">{t('accounts.records.noneHere')}</span>}
          <AttributeBadge attribute={a.patientIdAttribute} data-testid={`accounts-attribute-${a.id}`} />
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('accounts.columns.createdAt'),
      width: '7rem',
      render: (a) => (a.createdAt ? formatDate(a.createdAt) : EMPTY),
    },
  ];

  return (
    <section className="rounded-lg border border-border bg-card" aria-labelledby="accounts-accounts-title">
      <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
        <h2 id="accounts-accounts-title" className="text-sm font-bold text-foreground">
          {t('accounts.accountsTitle', { count: accounts.length })}
        </h2>
        <span className="text-xs text-muted-foreground" aria-live="polite">
          {t('accounts.selectHint', { count: selected.length })}
        </span>
        <div className="flex-1" />
        <Button
          size="sm"
          disabled={selected.length !== 2}
          onClick={onCompare}
          data-testid="accounts-compare"
        >
          <ArrowLeftRight size={14} aria-hidden />
          {t('accounts.compare')}
        </Button>
      </div>
      {accounts.length ? (
        <Table
          dense
          ariaLabel={t('accounts.accountsAria')}
          columns={columns}
          rows={accounts}
          rowKey={(a) => a.id}
          rowProps={(a) => ({ 'data-testid': `accounts-row-${a.id}` })}
          data-testid="accounts-table"
        />
      ) : (
        <p className="p-4 text-sm text-muted-foreground" data-testid="accounts-no-accounts">{t('accounts.noAccounts')}</p>
      )}
    </section>
  );
}

function RecordsTable({ records, onRelease, holderLabel }) {
  const { t, formatDate } = useTranslation();
  if (!records.length) return null;

  const columns = [
    {
      key: 'name',
      header: t('accounts.records.columns.name'),
      render: (r) => (
        <div className="min-w-0">
          <div dir="auto" className="truncate font-semibold text-foreground">{r.fullName || t('accounts.records.unnamed')}</div>
          <Ident value={r.id} className="text-muted-foreground" />
        </div>
      ),
    },
    { key: 'nationalId', header: t('accounts.records.columns.nationalId'), width: '8rem', render: (r) => <Ident value={r.nationalId} /> },
    { key: 'phone', header: t('accounts.records.columns.phone'), width: '9rem', render: (r) => <Ident value={r.phoneNumber} /> },
    {
      key: 'holder',
      header: t('accounts.records.columns.holder'),
      width: '12rem',
      render: (r) => (r.keycloakUserId
        ? <span dir="auto" className="truncate" data-testid={`accounts-record-holder-${r.id}`}>{holderLabel(r.keycloakUserId)}</span>
        : <span className="text-muted-foreground" data-testid={`accounts-record-holder-${r.id}`}>{t('accounts.records.unheld')}</span>),
    },
    { key: 'createdAt', header: t('accounts.records.columns.createdAt'), width: '7rem', render: (r) => (r.createdAt ? formatDate(r.createdAt) : EMPTY) },
    {
      key: 'actions',
      header: t('accounts.records.columns.actions'),
      width: '9rem',
      render: (r) => (r.keycloakUserId ? (
        <Button
          size="sm"
          variant="outline"
          className="text-destructive"
          onClick={() => onRelease(r)}
          aria-label={t('accounts.release.aria', { name: r.fullName || r.id })}
          data-testid={`accounts-release-${r.id}`}
        >
          <Unlink size={14} aria-hidden />
          {t('accounts.release.action')}
        </Button>
      ) : <span className="text-muted-foreground">{EMPTY}</span>),
    },
  ];

  return (
    <section className="rounded-lg border border-border bg-card" aria-labelledby="accounts-records-title">
      <div className="border-b border-border p-3">
        <h2 id="accounts-records-title" className="text-sm font-bold text-foreground">{t('accounts.records.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('accounts.records.sub')}</p>
      </div>
      <Table
        dense
        ariaLabel={t('accounts.records.aria')}
        columns={columns}
        rows={records}
        rowKey={(r) => r.id}
        rowProps={(r) => ({ 'data-testid': `accounts-record-${r.id}` })}
        data-testid="accounts-records"
      />
    </section>
  );
}
